import { styled } from "goober";
import {
  Fragment,
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { ConnectorWithOAuth } from "../../shared/connectors/getTargetValue";
import { connectorDirectory } from "../lib/connectorDirectory";
import { playClick } from "../lib/sounds";
import { ClickButton } from "./ClickButton";
import { ConnectorAvatarContextMenu } from "./ConnectorAvatarContextMenu";
import { ConnectorLogo } from "./ConnectorLogo";
import { ConnectorLogoContainer } from "./ConnectorLogoContainer";
import { FacePile } from "./FacePile";
import { Link } from "./Link";
import { Tooltip } from "./Tooltip";
import { makeUrl, navigate, routes } from "../lib/urls";

const FlatLogoButton = styled(ClickButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border-radius: 4px;
  transition: transform 0.1s ease-out;

  &:hover {
    transform: scale(1.15);
  }
`;

const FLAT_GAP = 4;
// Width reserved for the trailing "+N" badge when the logos overflow.
const FLAT_OVERFLOW_RESERVE = 24;

const FlatRow = styled("div")`
  display: flex;
  flex: 1;
  align-items: center;
  gap: ${FLAT_GAP}px;
  min-width: 0;
`;

export type ConnectedAvatar = {
  id: string;
  name: string;
  app: ConnectorWithOAuth;
  // When set, right-clicking the avatar opens a manage/remove context menu.
  onRemove?: () => void;
};

export type DisconnectedAvatar = {
  id: string;
  appType: string;
  // The reference name in the workflow's config — the only identity that
  // survives when the connector itself is gone.
  slug?: string;
  // Wraps the faded opener with a caller affordance (e.g. a reconnect dropdown);
  // read-only callers leave it inert.
  wrap?: (opener: ReactNode) => ReactNode;
};

type Props = {
  connected: readonly ConnectedAvatar[];
  disconnected?: readonly DisconnectedAvatar[];
  // Noun for the connected context menu, e.g. "step" or "chat".
  parentNoun?: string;
  // Gap in px below the avatar for the right-click context menu (default 4).
  menuMargin?: number;
  // Flat read-only strip (bare logos in a row) instead of the overlapping pile.
  flat?: boolean;
  // Collapse logos that overflow the available width into a trailing "+N" badge,
  // for width-constrained rows and columns (grid steps, list tables).
  collapseToFit?: boolean;
  // Flat only: horizontal alignment within the row. Defaults to left.
  align?: "start" | "end";
};

// The avatar pile shared by `ConnectorsBar` (which layers an add dropdown on
// top) and read-only views like grid steps. Resolved connectors open their app
// modal; referenced-but-missing types show faded.
export function ConnectorAvatars({
  connected,
  disconnected = [],
  parentNoun = "",
  menuMargin = 4,
  flat = false,
  collapseToFit = false,
  align = "start",
}: Props) {
  const size = 12;
  const itemSize = 24;
  const outlineWidth = 2;
  const background = true;
  const [appMenu, setAppMenu] = useState<{
    editTo: string;
    onRemove: () => void;
    right: number;
    top: number;
  } | null>(null);

  const contextMenu = appMenu && (
    <ConnectorAvatarContextMenu
      right={appMenu.right}
      top={appMenu.top}
      editTo={appMenu.editTo}
      parentNoun={parentNoun}
      onRemove={appMenu.onRemove}
      onClose={() => setAppMenu(null)}
    />
  );

  const contextMenuHandler = (
    id: string,
    onRemove: (() => void) | undefined
  ) =>
    onRemove
      ? (e: React.MouseEvent) => {
          e.preventDefault();
          playClick();
          const rect = e.currentTarget.getBoundingClientRect();
          setAppMenu({
            editTo: makeUrl(routes.modal.connector, { id: id }),
            onRemove,
            right: window.innerWidth - rect.right,
            top: rect.bottom + menuMargin,
          });
        }
      : undefined;

  const disconnectedMeta = (appType: string) => {
    const appDef = connectorDirectory.find(
      (a) => a.identifier.toLowerCase() === appType.toLowerCase()
    );
    return {
      name: appDef?.name ?? appType,
      identifier: appDef?.identifier ?? appType,
    };
  };

  if (flat) {
    if (connected.length === 0 && disconnected.length === 0) return null;
    const flatItems = [
      ...connected.map((app) => ({
        key: app.id,
        label: app.name,
        node: (
          <Tooltip content={app.name}>
            <FlatLogoButton
              data-card-chip
              onMouseDown={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                navigate(routes.modal.connector, { id: app.id });
              }}
              onContextMenu={contextMenuHandler(app.id, app.onRemove)}
            >
              <ConnectorLogo app={app.app} size={size} />
            </FlatLogoButton>
          </Tooltip>
        ),
      })),
      ...disconnected.map((slot) => {
        const { name, identifier } = disconnectedMeta(slot.appType);
        const label = slot.slug ?? name;
        const opener = (
          <Tooltip content={`Connect ${label}`}>
            <span style={{ display: "flex" }} data-card-chip>
              <ConnectorLogo
                template={{ name: label, identifier }}
                size={size}
                faded
              />
            </span>
          </Tooltip>
        );
        return {
          key: slot.id,
          label,
          node: slot.wrap ? slot.wrap(opener) : opener,
        };
      }),
    ];
    return (
      <>
        {collapseToFit ? (
          <FlatAvatarRow items={flatItems} itemSize={size} align={align} />
        ) : (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: align === "end" ? "flex-end" : "flex-start",
              gap: FLAT_GAP,
            }}
          >
            {flatItems.map((item) => (
              <Fragment key={item.key}>{item.node}</Fragment>
            ))}
          </div>
        )}
        {contextMenu}
      </>
    );
  }

  const connectedItems = connected.map((app) => {
    const manageTo = makeUrl(routes.modal.connector, { id: app.id });
    return {
      id: app.id,
      label: app.name,
      bare: true,
      node: (
        <Tooltip content={app.name}>
          <div
            style={{ cursor: "pointer" }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            onContextMenu={contextMenuHandler(app.id, app.onRemove)}
          >
            <ConnectorLogoContainer
              size={itemSize}
              outline={false}
              outlineWidth={outlineWidth}
              background={background}
              hoverBackground={background}
            >
              <Link to={manageTo} style={{ display: "flex" }}>
                <ConnectorLogo app={app.app} size={size} />
              </Link>
            </ConnectorLogoContainer>
          </div>
        </Tooltip>
      ),
    };
  });

  const disconnectedItems = disconnected.map((slot) => {
    const { name, identifier } = disconnectedMeta(slot.appType);
    const label = slot.slug ?? name;
    const opener = (
      <Tooltip content={`Connect ${label}`}>
        <ConnectorLogoContainer
          size={itemSize}
          outline={false}
          outlineWidth={outlineWidth}
          background={background}
          hoverBackground={background}
        >
          <ConnectorLogo
            template={{ name: label, identifier }}
            size={size}
            faded
          />
        </ConnectorLogoContainer>
      </Tooltip>
    );
    return {
      id: slot.id,
      label: `Connect ${label}`,
      bare: true,
      node: slot.wrap ? slot.wrap(opener) : opener,
    };
  });

  const items = [...connectedItems, ...disconnectedItems];
  if (items.length === 0) return null;

  return (
    <>
      <FacePile
        itemSize={itemSize}
        outlineWidth={outlineWidth}
        items={items}
        cutout
        collapseToFit={collapseToFit}
      />
      {contextMenu}
    </>
  );
}

// Width-aware flat logo strip: renders as many fixed-size logos as fit the
// available width and rolls the rest into a trailing "+N". Stays stable because
// `FlatRow` is `flex: 1`, so its width is set by its siblings, not its content.
function FlatAvatarRow({
  items,
  itemSize,
  align,
}: {
  items: { key: string; label: string; node: ReactNode }[];
  itemSize: number;
  align: "start" | "end";
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(items.length);

  const computeVisibleCount = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    const width = root.clientWidth;
    if (width === 0) return;

    const slot = itemSize + FLAT_GAP;
    const fitAll = Math.floor((width + FLAT_GAP) / slot);
    const next =
      fitAll >= items.length
        ? items.length
        : Math.max(0, Math.floor((width - FLAT_OVERFLOW_RESERVE) / slot));

    setVisibleCount((current) => (current === next ? current : next));
  }, [items.length, itemSize]);

  useLayoutEffect(() => {
    computeVisibleCount();
    const root = rootRef.current;
    if (!root) return;
    const observer = new ResizeObserver(computeVisibleCount);
    observer.observe(root);
    return () => observer.disconnect();
  }, [computeVisibleCount]);

  const visible = items.slice(0, visibleCount);
  const hidden = items.slice(visibleCount);

  return (
    <FlatRow
      ref={rootRef}
      style={{ justifyContent: align === "end" ? "flex-end" : "flex-start" }}
    >
      {visible.map((item) => (
        <Fragment key={item.key}>{item.node}</Fragment>
      ))}
      {hidden.length > 0 && (
        <Tooltip content={hidden.map((h) => h.label).join(", ")}>
          {/* Reuses GridStep's `.counter` class so the badge keeps its backdrop on card hover/selected. */}
          <div className="counter" data-card-chip>
            +{hidden.length}
          </div>
        </Tooltip>
      )}
    </FlatRow>
  );
}
